(() => {
  'use strict';

  const selector = '#collection-products[data-pp-continuous-collection]';
  let active = null;

  class ContinuousCollection {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('.collection-grid__wrapper > .grid--uniform');
      this.pagination = root.querySelector('[data-pp-collection-pagination]');
      this.status = root.querySelector('[data-pp-collection-status]');
      this.message = this.status?.querySelector('[data-pp-collection-message]');
      this.retry = this.status?.querySelector('[data-pp-collection-retry]');
      this.currentPage = Number(root.dataset.ppCurrentPage);
      this.pages = Number(root.dataset.ppPages);
      this.pageSize = Number(root.dataset.ppPageSize);
      this.total = Number(root.dataset.ppTotal);
      this.pageParam = root.dataset.ppPageParam || 'page';
      this.cursor = 1;
      this.running = false;
      this.destroyed = false;
      this.request = null;
      this.firstOriginalCard = this.cards()[0] || null;
      this.ids = new Set(this.cards().map(card => card.dataset.productId));
      this.seed = new URL(root.dataset.ppPageUrl || window.location.href, window.location.href);
      this.retry?.addEventListener('click', () => this.run());

      if (!this.grid || !Number.isInteger(this.pages) || this.pages < 1) return;
      if (this.pages === 1) {
        this.root.dataset.ppContinuousState = 'complete';
        return;
      }
      if (this.seed.origin !== window.location.origin) return;
      this.run();
    }

    cards() {
      return this.grid ? Array.from(this.grid.children).filter(node => node.matches('.grid-product[data-product-id]')) : [];
    }

    isCurrent() {
      return !this.destroyed && this.root.isConnected;
    }

    destroy() {
      this.destroyed = true;
      this.request?.abort();
    }

    setState(state) {
      this.root.dataset.ppContinuousState = state;
      if (this.pagination) this.pagination.hidden = state !== 'error';
      if (!this.status) return;
      this.status.hidden = state === 'complete';
      this.message.textContent = state === 'error' ? this.status.dataset.errorMessage : this.status.dataset.loadingMessage;
      this.retry.hidden = state !== 'error';
    }

    async load(page) {
      const url = new URL(this.seed.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set(this.pageParam, String(page));
      url.searchParams.set('section_id', this.root.dataset.ppSectionId);
      const controller = new AbortController();
      this.request = controller;
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url.href, {credentials: 'same-origin', signal: controller.signal});
        if (!response.ok) throw new Error('Collection response failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = document.querySelector(selector);
        const grid = root?.querySelector('.collection-grid__wrapper > .grid--uniform');
        if (!grid || Number(root.dataset.ppCurrentPage) !== page || Number(root.dataset.ppTotal) !== this.total) {
          throw new Error('Collection response changed');
        }
        const cards = Array.from(grid.children).filter(node => node.matches('.grid-product[data-product-id]'));
        const ids = cards.map(card => card.dataset.productId);
        const expectedCount = Math.min(this.pageSize, this.total - (page - 1) * this.pageSize);
        if (cards.length !== expectedCount || new Set(ids).size !== cards.length || ids.some(id => this.ids.has(id))) {
          throw new Error('Collection response is incomplete');
        }
        if (!this.isCurrent()) return;
        const fragment = window.document.createDocumentFragment();
        cards.forEach(card => {
          card.querySelectorAll('img').forEach(image => {
            image.loading = 'lazy';
            image.setAttribute('fetchpriority', 'auto');
          });
          fragment.appendChild(card);
          this.ids.add(card.dataset.productId);
        });
        if (page < this.currentPage) this.grid.insertBefore(fragment, this.firstOriginalCard);
        else this.grid.appendChild(fragment);
        this.activateCards(cards);
      } finally {
        window.clearTimeout(timeout);
        if (this.request === controller) this.request = null;
      }
    }

    activateCards(cards) {
      const theme = window.theme;
      cards.forEach(card => {
        if (theme?.Collection?.prototype.colorSwatchHovering) {
          theme.Collection.prototype.colorSwatchHovering.call({container: card});
        }
        if (theme?.settings.quickView && theme.preloadProductModal) {
          const open = () => {
            if (theme.config.bpSmall) return;
            card.removeEventListener('mouseover', open);
            card.removeEventListener('focusin', open);
            const button = card.querySelector('.quick-product__btn');
            if (button) theme.preloadProductModal(card.dataset.productHandle, card.dataset.productId, button);
          };
          card.addEventListener('mouseover', open);
          card.addEventListener('focusin', open);
        }
      });
      window.AOS?.refreshHard();
    }

    async run() {
      if (this.running || !this.isCurrent()) return;
      this.running = true;
      this.setState('loading');
      try {
        while (this.cursor <= this.pages && this.isCurrent()) {
          if (this.cursor !== this.currentPage) await this.load(this.cursor);
          this.cursor += 1;
        }
        if (!this.isCurrent()) return;
        if (this.ids.size !== this.total) throw new Error('Collection count changed');
        this.setState('complete');
      } catch (error) {
        if (this.isCurrent()) this.setState('error');
      } finally {
        this.running = false;
      }
    }
  }

  function initialize() {
    const root = document.querySelector(selector);
    if (active?.root === root) return;
    active?.destroy();
    active = root ? new ContinuousCollection(root) : null;
  }

  function start() {
    initialize();
    new MutationObserver(() => {
      if (!active || !active.root.isConnected || document.querySelector(selector) !== active.root) initialize();
    }).observe(document.body, {childList: true, subtree: true});
  }

  document.addEventListener('collection:reloaded', initialize);
  document.addEventListener('shopify:section:load', initialize);
  window.addEventListener('pageshow', initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
